import React from "react";

/**
 * Dot-pattern benefit icons extracted from the Vitura bottle label design.
 * Rendered as SVG circles so they color-shift via currentColor (works on
 * cream and dark backgrounds alike).
 */

const dot = (cx, cy, r = 3.4, key) => (
  <circle key={key} cx={cx} cy={cy} r={r} fill="currentColor" />
);

// 1. ENERGY — triangular accumulation, low → high (lightning rise)
function EnergyDots() {
  const rows = [
    { y: 78, count: 5 },
    { y: 60, count: 4 },
    { y: 42, count: 3 },
    { y: 24, count: 2 },
    { y: 8, count: 1 },
  ];
  const cx0 = 50;
  const gap = 12;
  return (
    <svg viewBox="0 0 100 90" aria-hidden="true">
      {rows.flatMap((row) => {
        const start = cx0 - ((row.count - 1) * gap) / 2;
        return Array.from({ length: row.count }).map((_, i) =>
          dot(start + i * gap, row.y, 3.4, `e-${row.y}-${i}`)
        );
      })}
    </svg>
  );
}

// 2. MOOD — circular orbit of dots
function MoodDots() {
  const cx = 50, cy = 45, r = 32, n = 10;
  return (
    <svg viewBox="0 0 100 90" aria-hidden="true">
      {Array.from({ length: n }).map((_, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        return dot(cx + Math.cos(a) * r, cy + Math.sin(a) * r, 3.4, `m-${i}`);
      })}
    </svg>
  );
}

// 3. COGNITION — clean 4×3 grid of dots
function CognitionDots() {
  const cols = 4, rows = 3;
  const stepX = 14, stepY = 14;
  const startX = 50 - ((cols - 1) * stepX) / 2;
  const startY = 45 - ((rows - 1) * stepY) / 2;
  const out = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      out.push(dot(startX + c * stepX, startY + r * stepY, 3.4, `c-${r}-${c}`));
    }
  }
  return <svg viewBox="0 0 100 90" aria-hidden="true">{out}</svg>;
}

// 4. RECOVERY / STRESS — flower pattern: 1 center + 8 surrounding
function RecoveryDots() {
  const cx = 50, cy = 45, r = 18;
  const out = [dot(cx, cy, 4.2, "r-c")];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
    out.push(dot(cx + Math.cos(a) * r, cy + Math.sin(a) * r, 3.4, `r-${i}`));
  }
  return <svg viewBox="0 0 100 90" aria-hidden="true">{out}</svg>;
}

const MAP = {
  energy: EnergyDots,
  mood: MoodDots,
  cognition: CognitionDots,
  recovery: RecoveryDots,
};

export default function BenefitIcon({ kind, className = "w-12 h-12" }) {
  const Cmp = MAP[kind] || EnergyDots;
  return (
    <div className={className} data-testid={`benefit-icon-${kind}`}>
      <Cmp />
    </div>
  );
}
